import { useCallback, useMemo } from 'react'

import { Interceptor } from '@connectrpc/connect'
import { TransportProvider } from '@connectrpc/connect-query'
import { createConnectTransport } from '@connectrpc/connect-web'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'

import { queryConfig } from '~/libs/reactQuery'
import { useAccessToken } from '~/provider/hooks/useAccessToken'

export const AppProvider = ({
  children,
}: {
  children: React.ReactNode
}): JSX.Element => {
  const { getAccessToken } = useAccessToken()
  const authInterceptor = useCallback<Interceptor>(
    (next) => async (req) => {
      const token = await getAccessToken()
      if (token != null && token.length > 0) {
        req.header.set('Authorization', `Bearer ${token}`)
      }
      return next(req)
    },
    [getAccessToken],
  )

  const transport = useMemo(() => {
    return createConnectTransport({
      baseUrl: import.meta.env.VITE_API_URL,
      interceptors: [authInterceptor],
    })
  }, [authInterceptor])

  const client = useMemo(() => {
    return new QueryClient({ defaultOptions: queryConfig })
  }, [])

  return (
    <TransportProvider transport={transport}>
      <QueryClientProvider client={client}>
        <ReactQueryDevtools initialIsOpen={false} />
        {children}
      </QueryClientProvider>
    </TransportProvider>
  )
}
